import { Injectable, OnDestroy } from '@angular/core';
import { AnalyticsService } from '@common/services';
import { CommerceService } from '@common/services/commerce.service';
import { ToastService } from '@common/services/toast.service';
import { UserService } from '@modules/auth/services';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, from, map, Observable, of, Subscription, switchMap, tap, throwError, timeout, timer } from 'rxjs';

/** Kill-switch de PostHog para impresión térmica de comandas. Fail-closed: ausente/off bloquea. */
export const COMANDAS_IMPRESION_FLAG = 'comandas-impresion';

export interface PrinterEntry {
    printerName: string;
    customName: string;
    paperSize: '58' | '80';
    default: boolean;
}

export interface ThermalPrinterConfig {
    active: boolean;
    printers: PrinterEntry[];
}

export interface EscPosOperation {
    nombre: string;
    argumentos: any[];
}

interface PrintRequest {
    operaciones: EscPosOperation[];
    nombreImpresora: string;
}

/** Línea del ticket de comanda: cantidad, producto y una nota opcional (variaciones u observación). */
export interface ComandaTicketItem {
    qty: number | string;
    name: string;
    note?: string | null;
}

/**
 * Datos de un ticket de comanda, ya normalizados por quien lo dispara. El servicio solo los
 * traduce a operaciones ESC/POS: no conoce el modelo de venta ni el estado del carrito.
 */
export interface ComandaTicketData {
    title: string;
    subtitle?: string | null;
    table?: string | null;
    order?: string | null;
    time?: string | null;
    waiter?: string | null;
    customer?: string | null;
    items: ComandaTicketItem[];
    waiterNote?: string | null;
}

export interface ApiResponse<T> {
    title: string;
    message: string;
    data: T;
}

interface PrintResult {
    printerName: string;
}

/** Motivo por el que el servicio se niega a imprimir antes de llegar al plugin. */
export type PrintBlockedReason = 'thermal_printing_disabled' | 'print_server_offline' | 'feature_flag_disabled';

/**
 * Error con el que el servicio rechaza una impresión que no cumple los requisitos previos. Ya
 * viene avisado al usuario con un toast, así que quien lo recibe no debe volver a notificarlo.
 */
export interface PrintBlockedError {
    printBlocked: true;
    reason: PrintBlockedReason;
    title: string;
    message: string;
}

export function isPrintBlockedError(error: any): error is PrintBlockedError {
    return error?.printBlocked === true;
}

const STORAGE_KEY = 'thermalPrinters';
const POLL_INTERVAL_MS = 30_000;
const PRINT_REQUEST_TIMEOUT_MS = 10000;
const SERVER_CHECK_TIMEOUT_MS = 5000;
const MAX_CONFIGURED_PRINTERS = 10;

/**
 * Ventana de lectura del aviso de bloqueo. Son dos frases (~110 caracteres) con la instrucción para
 * arreglarlo, y el usuario las lee en medio de una venta: con los 4 s por defecto del toast no llega.
 */
const BLOCKED_PRINT_TOAST_DELAY_MS = 6000;

const BLOCKED_PRINT_MESSAGES: Record<Exclude<PrintBlockedReason, 'feature_flag_disabled'>, { title: string; message: string }> = {
    thermal_printing_disabled: {
        title: 'Impresión térmica desactivada',
        message: 'El comercio tiene la impresión térmica desactivada. Actívala en Comercio > Configuración de impresora para poder imprimir.',
    },
    print_server_offline: {
        title: 'Fina Print no está disponible',
        message: 'No se pudo conectar con Fina Print en este equipo. Ábrelo y verifica que esté activo para volver a imprimir.',
    },
};

/**
 * Capacidad medida del papel a tamaño de fuente base (fuente A), tomada de impresiones reales.
 * Los separadores y las filas Pedido/Hora/Mesero usan este ancho; el contenido textual se
 * presupuesta con margen aparte para no llegar a los bordes.
 */
const LINE_WIDTH_BY_PAPER_SIZE: Record<'58' | '80', number> = { '58': 48, '80': 47 };

/** Margen lateral reservado al contenido. Los separadores siguen usando el ancho completo. */
const CONTENT_MARGIN_CHARS = 2;

/** Escalas de la línea de mesa, de mayor a menor: se prefiere una mesa más pequeña a una cortada. */
const TABLE_FONT_SCALES = [3, 2, 1];

/** Líneas máximas para el nombre de un producto antes de recortar. */
const MAX_ITEM_NAME_LINES = 3;

/** ASCII, no `…`: el carácter depende de la codepage de la impresora. */
const ELLIPSIS = '...';

const ALIGN = { left: 0, center: 1, right: 2 };
const FONT = { A: 0, B: 1 };

const DEFAULT_PRINT_URL = 'http://127.0.0.1:8000';

/** Chrome Local Network Access: public HTTPS pages must declare loopback intent on fetch(). */
type LoopbackRequestInit = RequestInit & { targetAddressSpace?: 'loopback' | 'local' | 'public' };

@Injectable({ providedIn: 'root' })
export class FinaPrintService implements OnDestroy {
    private readonly printUrl: string = (environment as { finaPrintUrl?: string }).finaPrintUrl || DEFAULT_PRINT_URL;
    private pollingSubscription: Subscription | null = null;
    private readonly visibilityChangeHandler = () => this.updatePollingForVisibility();
    private tenantId: string | null = null;

    serverConnected$ = new BehaviorSubject<boolean>(false);

    constructor(private commerceService: CommerceService, private userService: UserService, private toastService: ToastService, private analyticsService: AnalyticsService) {}

    ngOnDestroy(): void {
        this.stopPolling();
    }

    setTenantId(tenantId: string): void {
        this.tenantId = tenantId || null;
    }

    /** Puerto en el que responde el plugin local. Null cuando la URL configurada no declara puerto. */
    get serverPort(): number | null {
        try {
            const port = Number(new URL(this.printUrl).port);
            return port > 0 ? port : null;
        } catch {
            return null;
        }
    }

    startPolling(): void {
        this.stopPolling();
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        this.updatePollingForVisibility();
    }

    stopPolling(): void {
        this.pollingSubscription?.unsubscribe();
        this.pollingSubscription = null;
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    private updatePollingForVisibility(): void {
        this.pollingSubscription?.unsubscribe();
        this.pollingSubscription = null;

        if (document.hidden) return;

        this.pollingSubscription = timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() =>
                    this.pluginRequest<ApiResponse<string[]>>('/impresoras', {}, SERVER_CHECK_TIMEOUT_MS).pipe(
                        map(response => response.data),
                        catchError(() => of(null)),
                    ),
                ),
                tap(result => this.serverConnected$.next(result !== null)),
            )
            .subscribe();
    }

    checkServerOnce(): Observable<boolean> {
        return this.probeServer();
    }

    /**
     * Verifica contra el plugin local si el servidor de impresión está activo y publica el
     * resultado en `serverConnected$`. Nunca emite error: el estado de conexión es el resultado.
     */
    private probeServer(): Observable<boolean> {
        return this.pluginRequest<ApiResponse<string[]>>('/impresoras', {}, SERVER_CHECK_TIMEOUT_MS).pipe(
            map(() => true),
            catchError(() => of(false)),
            tap(connected => this.serverConnected$.next(connected)),
        );
    }

    /**
     * Talks to the local plugin via fetch so Chrome can be told this is loopback
     * (`targetAddressSpace`). Angular HttpClient uses XHR and cannot set that option.
     */
    private pluginRequest<T>(path: string, init: RequestInit, timeoutMs: number): Observable<T> {
        return from(this.pluginFetch(path, init)).pipe(
            timeout(timeoutMs),
            switchMap(async response => {
                if (!response.ok) {
                    throw { status: response.status };
                }
                return (await response.json()) as T;
            }),
        );
    }

    private pluginFetch(path: string, init: RequestInit = {}): Promise<Response> {
        return fetch(`${this.printUrl}${path}`, {
            ...init,
            targetAddressSpace: 'loopback',
        } as LoopbackRequestInit);
    }

    /**
     * Preferencia del comercio en el modelo del tenant (Comercio > Configuración de impresora). `_commerce$`
     * solo se llena cuando alguna pantalla carga el comercio, así que los flujos que imprimen sin
     * pasar por ahí (el POS con las comandas) se apoyan en el tenant sembrado durante el login.
     */
    isThermalPrintingEnabled(): boolean {
        const tenant = this.commerceService._commerce$.value ?? this.userService.tenant$.value;
        return !!tenant?.thermalPrinter;
    }

    /**
     * Avisa al usuario y rechaza la impresión con el motivo, sin tocar el plugin. El aviso es el
     * ÚNICO que explica que la impresión está apagada, así que se protege de ser reemplazado: en el
     * flujo de comandas se dispara justo después del toast de éxito y cualquier otra operación del
     * carrito lo borraría antes de leerse.
     */
    private blockPrint(reason: Exclude<PrintBlockedReason, 'feature_flag_disabled'>): Observable<never> {
        const { title, message } = BLOCKED_PRINT_MESSAGES[reason];
        this.toastService.error(title, message, { delay: BLOCKED_PRINT_TOAST_DELAY_MS, keepOnNewToast: true });

        return throwError(() => ({ printBlocked: true, reason, title, message } as PrintBlockedError));
    }

    getAvailablePrinters(): Observable<string[]> {
        return this.pluginRequest<ApiResponse<string[]>>('/impresoras', {}, SERVER_CHECK_TIMEOUT_MS).pipe(
            map(response => response.data),
            tap(() => this.serverConnected$.next(true)),
            catchError(() => {
                this.serverConnected$.next(false);
                return of([] as string[]);
            }),
        );
    }

    /**
     * Punto ÚNICO de comunicación con el plugin para imprimir: recibe operaciones ESC/POS ya
     * construidas y las envía a la impresora indicada. El plugin responde con error HTTP (400/500)
     * cuando no hay impresora seleccionada o el hardware falla, así que el error se propaga.
     *
     * Antes de enviar valida los dos requisitos que no dependen del hardware: que el comercio tenga
     * la impresión térmica habilitada y que el servidor local de Fina Print esté activo. El estado
     * del servidor se consulta en vivo porque `serverConnected$` solo se mantiene al día mientras
     * hay polling (pantalla de configuración), y quien imprime normalmente no lo tiene corriendo.
     */
    sendOperations(printerName: string, operaciones: EscPosOperation[]): Observable<string> {
        if (!this.isThermalPrintingEnabled()) return this.blockPrint('thermal_printing_disabled');

        return this.probeServer().pipe(switchMap(connected => (connected ? this.postOperations(printerName, operaciones) : this.blockPrint('print_server_offline'))));
    }

    private postOperations(printerName: string, operaciones: EscPosOperation[]): Observable<string> {
        const body: PrintRequest = { nombreImpresora: printerName, operaciones };

        return this.pluginRequest<ApiResponse<PrintResult>>(
            '/imprimir',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            },
            PRINT_REQUEST_TIMEOUT_MS,
        ).pipe(map(response => response.data.printerName));
    }

    printTestTicket(printerName: string, paperSize: '58' | '80'): Observable<string> {
        const separator = '-'.repeat(LINE_WIDTH_BY_PAPER_SIZE[paperSize]);

        return this.sendOperations(printerName, [
            { nombre: 'Iniciar', argumentos: [] },
            { nombre: 'EstablecerAlineacion', argumentos: [ALIGN.center] },
            { nombre: 'EstablecerTamañoFuente', argumentos: [2, 2] },
            { nombre: 'EscribirTexto', argumentos: ['FINA\n'] },
            { nombre: 'EstablecerTamañoFuente', argumentos: [1, 1] },
            { nombre: 'EscribirTexto', argumentos: ['Ticket de prueba\n'] },
            { nombre: 'EstablecerAlineacion', argumentos: [ALIGN.left] },
            { nombre: 'EscribirTexto', argumentos: [`${separator}\n`] },
            { nombre: 'EscribirTexto', argumentos: ['Impresora: ' + printerName + '\n'] },
            { nombre: 'EscribirTexto', argumentos: ['Papel: ' + paperSize + ' mm\n'] },
            { nombre: 'EscribirTexto', argumentos: [`${separator}\n`] },
            { nombre: 'EstablecerAlineacion', argumentos: [ALIGN.center] },
            { nombre: 'EscribirTexto', argumentos: ['Configuracion exitosa\n'] },
            { nombre: 'Feed', argumentos: [3] },
            { nombre: 'Corte', argumentos: [5] },
        ]);
    }

    /** Imprime una comanda en la impresora indicada, respetando su ancho de papel. */
    printComanda(ticket: ComandaTicketData, printer: PrinterEntry): Observable<string> {
        if (!this.analyticsService.getExperimentVariant(COMANDAS_IMPRESION_FLAG)) {
            return throwError(
                () =>
                    ({
                        printBlocked: true,
                        reason: 'feature_flag_disabled',
                        title: '',
                        message: '',
                    } as PrintBlockedError),
            );
        }

        return this.sendOperations(printer.printerName, this.buildComandaOperations(ticket, printer.paperSize));
    }

    /**
     * Traduce un ticket de comanda al layout de referencia del plugin
     * (`escpos-plugin/example/simple/script.js`): encabezado, mesa destacada, datos del pedido,
     * lista de ítems con notas indentadas, nota del mesero y pie.
     */
    buildComandaOperations(ticket: ComandaTicketData, paperSize: '58' | '80'): EscPosOperation[] {
        const width = LINE_WIDTH_BY_PAPER_SIZE[paperSize];
        const contentWidth = width - CONTENT_MARGIN_CHARS;
        const operations: EscPosOperation[] = [{ nombre: 'Iniciar', argumentos: [] }];

        const write = (text: string) => operations.push({ nombre: 'EscribirTexto', argumentos: [text] });
        const align = (value: number) => operations.push({ nombre: 'EstablecerAlineacion', argumentos: [value] });
        const font = (value: number) => operations.push({ nombre: 'EstablecerFuente', argumentos: [value] });
        const emphasize = (value: boolean) => operations.push({ nombre: 'EstablecerEnfatizado', argumentos: [value] });
        const fontSize = (widthScale: number, heightScale: number) => operations.push({ nombre: 'EstablecerTamañoFuente', argumentos: [widthScale, heightScale] });
        const divider = (char: string) => {
            align(ALIGN.left);
            write(`${char.repeat(width)}\n`);
        };
        const writeWrapped = (text: string, lineWidth: number, indent = '', maxLines = Number.POSITIVE_INFINITY) => {
            this.wrapText(text, lineWidth, maxLines).forEach(line => write(`${indent}${line}\n`));
        };

        align(ALIGN.center);
        emphasize(true);
        write(`${ticket.title}\n`);
        emphasize(false);
        if (ticket.subtitle) writeWrapped(ticket.subtitle, contentWidth);

        if (ticket.table) {
            divider('=');
            align(ALIGN.center);
            font(FONT.B);
            write('M E S A\n');
            font(FONT.A);
            emphasize(true);
            // Escala adaptativa: a Nx el ancho útil es contentWidth / N; se centra con la alineación.
            const { text, scale } = this.fitScaledLine(ticket.table, contentWidth, TABLE_FONT_SCALES);
            fontSize(scale, scale);
            write(`${text}\n`);
            fontSize(1, 1);
            emphasize(false);
            divider('=');
        }

        align(ALIGN.left);
        const infoRows: [string, string | null | undefined][] = [
            ['Pedido #', ticket.order],
            ['Hora', ticket.time],
            ['Mesero', ticket.waiter],
            ['Cliente', ticket.customer],
        ];
        infoRows.filter(([, value]) => value !== null && value !== undefined && value !== '').forEach(([label, value]) => write(`${this.buildRow(label, String(value), contentWidth)}\n`));

        divider('-');
        align(ALIGN.left);
        font(FONT.B);
        write('Cant.  Item\n');
        font(FONT.A);
        divider('-');

        align(ALIGN.left);
        ticket.items.forEach(({ qty, name, note }) => {
            const prefix = `${qty}x  `;
            const nameWidth = Math.max(1, contentWidth - prefix.length);
            const nameLines = this.wrapText(name, nameWidth, MAX_ITEM_NAME_LINES);
            nameLines.forEach((line, index) => write(`${index === 0 ? prefix : ' '.repeat(prefix.length)}${line}\n`));

            if (note) {
                const noteIndent = '    ';
                font(FONT.B);
                // Fuente B es más angosta: presupuestar contra fuente A queda conservador y no desborda.
                writeWrapped(note, Math.max(1, contentWidth - noteIndent.length), noteIndent);
                font(FONT.A);
            }
        });
        divider('-');

        if (ticket.waiterNote) {
            align(ALIGN.left);
            font(FONT.B);
            write('Nota del mesero:\n');
            font(FONT.A);
            writeWrapped(`"${ticket.waiterNote}"`, contentWidth);
        }

        divider('=');
        align(ALIGN.center);
        font(FONT.B);
        if (ticket.time) write(`Impreso: ${ticket.time}\n`);
        write('FINA PARTNER - COMANDAS\n');
        font(FONT.A);

        operations.push({ nombre: 'Feed', argumentos: [3] });
        operations.push({ nombre: 'Corte', argumentos: [5] });

        return operations;
    }

    /** Recorta con elipsis cuando el texto no cabe en `max` caracteres. */
    private truncate(text: string, max: number): string {
        if (max <= 0) return '';
        if (text.length <= max) return text;
        if (max <= ELLIPSIS.length) return ELLIPSIS.slice(0, max);
        return text.slice(0, max - ELLIPSIS.length) + ELLIPSIS;
    }

    /**
     * Parte el texto en líneas de hasta `width` caracteres. Las palabras más largas que el ancho se
     * cortan; si se agota `maxLines`, la última línea se recorta con elipsis.
     */
    private wrapText(text: string, width: number, maxLines = Number.POSITIVE_INFINITY): string[] {
        if (width <= 0) return [];

        const words = text.trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) return [''];

        const lines: string[] = [];
        let current = '';

        const remainingBudget = () => maxLines - lines.length;

        const flushOverflow = (): boolean => {
            while (current.length > width) {
                if (remainingBudget() <= 1) {
                    lines.push(this.truncate(current, width));
                    current = '';
                    return true;
                }
                lines.push(current.slice(0, width));
                current = current.slice(width);
            }
            return false;
        };

        for (const word of words) {
            if (remainingBudget() <= 0) break;

            const candidate = current ? `${current} ${word}` : word;
            if (candidate.length <= width) {
                current = candidate;
                continue;
            }

            if (current) {
                if (remainingBudget() <= 1) {
                    lines.push(this.truncate(candidate, width));
                    current = '';
                    break;
                }
                lines.push(current);
                current = word;
                if (flushOverflow()) break;
                continue;
            }

            current = word;
            if (flushOverflow()) break;
        }

        if (current && remainingBudget() > 0) {
            lines.push(current.length > width ? this.truncate(current, width) : current);
        }

        return lines.length ? lines : [''];
    }

    /**
     * Elige la mayor escala donde el texto cabe en contentWidth / scale. Si ninguna alcanza, usa la
     * menor y recorta con elipsis.
     */
    private fitScaledLine(text: string, contentWidth: number, scales: number[]): { text: string; scale: number } {
        const ordered = scales.length ? scales : [1];
        for (const scale of ordered) {
            const capacity = Math.floor(contentWidth / scale);
            if (text.length <= capacity) return { text, scale };
        }

        const scale = ordered[ordered.length - 1];
        const capacity = Math.max(1, Math.floor(contentWidth / scale));
        return { text: this.truncate(text, capacity), scale };
    }

    /** Etiqueta a la izquierda y valor a la derecha dentro del ancho de contenido. */
    private buildRow(label: string, value: string, width: number): string {
        const valueBudget = Math.max(1, width - label.length - 1);
        const fittedValue = this.truncate(value, valueBudget);
        const space = width - label.length - fittedValue.length;
        return label + ' '.repeat(Math.max(1, space)) + fittedValue;
    }

    /**
     * Impresora térmica a usar por defecto. Devuelve null cuando el módulo está inactivo o no hay
     * impresoras configuradas, para que quien imprime pueda avisar sin tratarlo como fallo de hardware.
     */
    getDefaultPrinter(): PrinterEntry | null {
        const config = this.loadConfig();
        if (!config?.active || !config.printers?.length) return null;
        return config.printers.find(printer => printer.default) ?? config.printers[0];
    }

    saveConfig(config: ThermalPrinterConfig): void {
        localStorage.setItem(this.storageKey, JSON.stringify(config));
    }

    loadConfig(): ThermalPrinterConfig | null {
        try {
            const raw = localStorage.getItem(this.storageKey);
            const usingLegacyStorage = !raw && this.storageKey !== STORAGE_KEY;
            const configRaw = raw ?? (usingLegacyStorage ? localStorage.getItem(STORAGE_KEY) : null);
            if (!configRaw) return null;
            const parsed = JSON.parse(configRaw);
            if (!this.isValidConfigShape(parsed)) {
                localStorage.removeItem(usingLegacyStorage ? STORAGE_KEY : this.storageKey);
                return null;
            }

            const printers = parsed.printers.map((printer: unknown) => this.normalizePrinter(printer)).filter((printer: PrinterEntry | null): printer is PrinterEntry => printer !== null);
            const uniquePrinters = printers.filter((printer, index) => printers.findIndex(candidate => candidate.printerName === printer.printerName) === index);

            if (uniquePrinters.length === 0) {
                localStorage.removeItem(usingLegacyStorage ? STORAGE_KEY : this.storageKey);
                return null;
            }

            const defaultIndex = uniquePrinters.findIndex(printer => printer.default);
            const normalizedPrinters = uniquePrinters.map((printer, index) => ({
                ...printer,
                default: index === (defaultIndex === -1 ? 0 : defaultIndex),
            }));
            const config: ThermalPrinterConfig = { active: parsed.active, printers: normalizedPrinters };

            if (usingLegacyStorage || JSON.stringify(parsed) !== JSON.stringify(config)) {
                this.saveConfig(config);
                if (usingLegacyStorage) localStorage.removeItem(STORAGE_KEY);
            }

            return config;
        } catch {
            localStorage.removeItem(this.storageKey);
            return null;
        }
    }

    clearConfig(): void {
        localStorage.removeItem(this.storageKey);
    }

    private get storageKey(): string {
        return this.tenantId ? `${STORAGE_KEY}:${this.tenantId}` : STORAGE_KEY;
    }

    private isValidConfigShape(config: unknown): config is { active: boolean; printers: unknown[] } {
        if (!config || typeof config !== 'object') return false;

        const candidate = config as { active?: unknown; printers?: unknown };
        return typeof candidate.active === 'boolean' && Array.isArray(candidate.printers) && candidate.printers.length <= MAX_CONFIGURED_PRINTERS;
    }

    private normalizePrinter(printer: unknown): PrinterEntry | null {
        if (!printer || typeof printer !== 'object') return null;

        const candidate = printer as Partial<PrinterEntry>;
        if (
            typeof candidate.printerName !== 'string' ||
            typeof candidate.customName !== 'string' ||
            typeof candidate.default !== 'boolean' ||
            (candidate.paperSize !== '58' && candidate.paperSize !== '80')
        ) {
            return null;
        }

        const printerName = candidate.printerName.trim();
        if (!printerName) return null;

        return {
            printerName,
            customName: candidate.customName.trim().slice(0, 40) || printerName,
            paperSize: candidate.paperSize,
            default: candidate.default,
        };
    }
}
